import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-onboarding-progress.css?inline';

/**
 * `<pk-onboarding-progress>` — step indicator for multi-step flows.
 *
 * Renders a horizontal sequence of circle markers connected by a line, with a
 * label below each marker. Steps before `current` are filled (completed),
 * the `current` step is highlighted, and later steps are pending.
 *
 * Steps can be supplied either as:
 *   - a JSON-array attribute (`steps='["Register","Verify","Onboard"]'`), or
 *   - a comma-separated string (`steps="Register,Verify,Onboard"`).
 *
 * Attributes:
 *   - `steps`   — JSON array OR comma-separated string of step labels.
 *   - `current` — 1-based index of the current step (default 1). Out-of-range
 *                 values clamp to [1, steps.length].
 */
export class PkOnboardingProgress extends HTMLElement {
    static readonly tagName = 'pk-onboarding-progress';

    static get observedAttributes(): string[] {
        return ['steps', 'current'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private _steps(): string[] {
        const raw = (this.getAttribute('steps') ?? '').trim();
        if (!raw) return [];
        // Try JSON array first.
        if (raw.startsWith('[')) {
            try {
                const parsed = JSON.parse(raw) as unknown;
                if (Array.isArray(parsed)) {
                    return parsed.map((s) => String(s)).filter((s) => s.length > 0);
                }
            } catch {
                /* fall through to comma-split */
            }
        }
        return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    }

    private _current(steps: string[]): number {
        const n = Number(this.getAttribute('current') ?? '1');
        if (!Number.isFinite(n)) return 1;
        if (steps.length === 0) return 1;
        return Math.min(Math.max(Math.trunc(n), 1), steps.length);
    }

    private template(): TemplateResult {
        const steps   = this._steps();
        const current = this._current(steps);

        return html`
            <style>${styles}</style>
            <ol class="track" aria-label="Progress">
                ${steps.map((label, idx) => {
                    const oneBased = idx + 1;
                    const state    = oneBased < current
                        ? 'done'
                        : oneBased === current
                            ? 'current'
                            : 'pending';
                    const ariaCurrent = state === 'current' ? 'step' : undefined;
                    return html`
                        <li class="step state-${state}"
                            aria-current=${ariaCurrent ?? ''}>
                            <span class="marker" aria-hidden="true">
                                ${state === 'done' ? '✓' : oneBased}
                            </span>
                            <span class="label">${label}</span>
                        </li>
                    `;
                })}
            </ol>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkOnboardingProgress.tagName)) {
    customElements.define(PkOnboardingProgress.tagName, PkOnboardingProgress);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-onboarding-progress': PkOnboardingProgress;
    }
}
